'use client'

import { useRef, useState } from 'react'

import { useAnalytics } from '@/hooks/useAnalytics'
import { useMountEffect } from '@/hooks/useMountEffect'
import type { ProjectReel } from '@/lib/projectsData'

function SoundIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" width="14">
      <path d="M3.5 7.5h2.8L10 4.5v11l-3.7-3H3.5v-5Z" fill="currentColor" />
      {on ? (
        <path
          d="M13 7.2a4 4 0 0 1 0 5.6M15.2 5a7 7 0 0 1 0 10"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="m13.2 8 4 4m0-4-4 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      )}
    </svg>
  )
}

const clamp01 = (t: number) => Math.min(1, Math.max(0, t))
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)
const span = (t: number, from: number, to: number) =>
  clamp01((t - from) / (to - from))

// How far through the pinned stage the reader is, 0 to 1.
function stageProgress(stage: HTMLElement, stick: HTMLElement) {
  const rect = stage.getBoundingClientRect()
  return clamp01(-rect.top / Math.max(1, rect.height - stick.offsetHeight))
}

// The hero's reel. It opens full width, then settles into a framed card
// while the title fades in under it and the accessory climbs up behind.
// Only three numbers change on scroll; story.css turns them into the layout.
export function StoryHeroMedia({
  video,
  name,
  accessory,
  children,
}: {
  video: ProjectReel
  name: string
  accessory?: React.ReactNode
  children: React.ReactNode
}) {
  const stageRef = useRef<HTMLDivElement>(null)
  const stickRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [soundOn, setSoundOn] = useState(false)
  const { track } = useAnalytics()

  useMountEffect(() => {
    const stage = stageRef.current
    const stick = stickRef.current
    if (!stage || !stick) return
    let frame = 0
    let last = -1
    const update = () => {
      const t = stageProgress(stage, stick)
      if (t === last) return
      last = t
      stage.style.setProperty('--settle', easeOut(span(t, 0, 0.55)).toFixed(4))
      stage.style.setProperty(
        '--climb',
        easeOut(span(t, 0.55, 0.75)).toFixed(4),
      )
      stage.style.setProperty('--title', span(t, 0.5, 0.75).toFixed(4))
    }
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  })

  // React sets `muted` as a property after hydration, so the server-rendered
  // autoplay can be refused; retrying once data is ready covers that.
  const handleLoadedData = () => {
    videoRef.current?.play().catch(() => {})
  }

  const toggleSound = () => {
    const el = videoRef.current
    if (!el) return
    const next = !soundOn
    el.muted = !next
    if (next) {
      el.currentTime = 0
      el.play().catch(() => {})
      track('content_engagement', {
        type: 'video_play',
        details: `reel_sound_on:${name}`,
      })
    }
    setSoundOn(next)
  }

  return (
    <div ref={stageRef} className="story-hero-stage">
      <div ref={stickRef} className="story-hero-stick">
        {accessory && <div className="story-hero-accessory">{accessory}</div>}
        <div
          className="story-hero-frame"
          style={{ backgroundColor: video.background }}
        >
          <video
            ref={videoRef}
            poster={video.poster}
            aria-label={`${name} animated overview`}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={handleLoadedData}
          >
            {/* Browsers without an AV1 decoder skip this on the codecs string
              and play the H.264 file. */}
            {video.av1Src && (
              <source
                src={video.av1Src}
                type='video/mp4; codecs="av01.0.08M.08"'
              />
            )}
            <source src={video.src} type="video/mp4" />
          </video>
          {video.hasAudio && (
            <button
              type="button"
              className="story-hero-sound"
              onClick={toggleSound}
              aria-pressed={soundOn}
              aria-label={
                soundOn ? 'Mute overview' : 'Play overview with sound'
              }
            >
              <SoundIcon on={soundOn} />
              {soundOn ? 'Sound on' : 'Sound'}
            </button>
          )}
        </div>
        <div className="story-hero-title">{children}</div>
      </div>
    </div>
  )
}
